import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, LogIn, Lock, User, AlertTriangle, X, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import AnimatedLogo from '../components/AnimatedLogo';
import { authAPI } from '../services/api';

const LoginPage = () => {
    const [formData, setFormData] = useState({ username: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [showSessionModal, setShowSessionModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const [recaptchaKey, setRecaptchaKey] = useState('');

    // Phase 1: Dynamic reCAPTCHA Loading
    useEffect(() => {
        authAPI.getRecaptchaConfig()
            .then(res => {
                if (res.data.success && res.data.siteKey) {
                    setRecaptchaKey(res.data.siteKey);
                    
                    // Inject script if not already present
                    if (!document.getElementById('recaptcha-v3-script')) {
                        const script = document.createElement('script');
                        script.id = 'recaptcha-v3-script';
                        script.src = `https://www.google.com/recaptcha/api.js?render=${res.data.siteKey}`;
                        script.async = true;
                        document.body.appendChild(script);
                    }
                }
            })
            .catch(err => console.error('Error fetching reCAPTCHA config:', err));
    }, []);

    const getRecaptchaToken = async () => {
        if (!recaptchaKey) return null;
        if (!window.grecaptcha) {
            console.warn('reCAPTCHA not loaded yet');
            return null;
        }

        return new Promise((resolve) => {
            window.grecaptcha.ready(async () => {
                try {
                    const token = await window.grecaptcha.execute(recaptchaKey, { action: 'login' });
                    resolve(token);
                } catch (err) {
                    console.error('reCAPTCHA execution error:', err);
                    resolve(null);
                }
            });
        });
    };

    const handleSubmit = async (e, force = false) => {
        if (e) e.preventDefault();

        if (!formData.username || !formData.password) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }
        setLoading(true);
        try {
            // Get reCAPTCHA token
            const recaptcha_token = await getRecaptchaToken();
            
            const res = await login({ 
                ...formData, 
                force, 
                recaptcha_token 
            });
            
            // Phase 2: Handle mandatory password change
            if (res?.status === 'must_reset') {
                navigate('/reset-password', { 
                    state: { 
                        username: formData.username, 
                        tempPassword: formData.password 
                    } 
                });
                return;
            }

            // Phase 4: Handle 2FA
            if (res?.status === '2fa_required') {
                navigate('/verify-2fa', { state: { userId: res.userId } });
                return;
            }

            toast.success('Connexion réussie ! Bienvenue.');
            navigate('/');
        } catch (err) {
            if (err.response?.status === 409 && err.response?.data?.code === 'ALREADY_CONNECTED') {
                setShowSessionModal(true);
            } else {
                const msg = err.response?.data?.message || 'Erreur de connexion';
                toast.error(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page-v2">
            {/* Background elements */}
            <div className="login-bg-hero"></div>
            <div className="login-bg-overlay"></div>
            <div className="login-glow-1"></div>
            <div className="login-glow-2"></div>

            <main className="glass-login-card">
                <div className="login-brand">
                    <div className="login-brand-logo">
                        <AnimatedLogo height="180px" />
                    </div>
                    <h1>Expertise H.B</h1>
                    <div className="login-brand-line"></div>
                    <p>Système de gestion d'expertise automobile</p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="login-form-group">
                        <label className="login-label">Identifiant Académique</label>
                        <div className="login-input-wrapper">
                            <User className="login-input-icon" size={18} />
                            <input
                                type="text"
                                className="login-input"
                                placeholder="Nom d'utilisateur"
                                value={formData.username}
                                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                autoComplete="username"
                                autoFocus
                            />
                        </div>
                    </div>

                    <div className="login-form-group">
                        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '8px' }}>
                            <label className="login-label">Clé de Sécurité</label>
                        </div>
                        <div className="login-input-wrapper">
                            <Lock className="login-input-icon" size={18} />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                className="login-input"
                                style={{ paddingRight: '48px' }}
                                placeholder="••••••••"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)' }}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="login-btn"
                        disabled={loading}
                    >
                        {loading ? (
                            <><div className="spinner" style={{ width: '18px', height: '18px', borderLeftColor: '#071325' }} /> Vérification...</>
                        ) : (
                            <><ShieldCheck size={20} /> Accès Sécurisé</>
                        )}
                    </button>
                    
                    <div style={{ textAlign: 'center', marginTop: '24px' }}>
                        <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.5px' }}>
                            Accès restreint aux experts agréés • Algérie 2026
                        </p>
                    </div>
                </form>
            </main>



            {showSessionModal && (
                <div className="modal-overlay" style={{ zIndex: 2000 }}>
                    <div className="modal" style={{ maxWidth: '450px', background: '#0a1628', border: '1px solid rgba(212, 175, 55, 0.2)', color: 'white' }}>
                        <div className="modal-header" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                            <h3 className="modal-title" style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <AlertTriangle size={20} /> Session déjà active
                            </h3>
                            <button className="modal-close" onClick={() => setShowSessionModal(false)} style={{ color: 'white' }}><X size={18} /></button>
                        </div>
                        <div className="modal-body" style={{ textAlign: 'center', padding: '20px 30px' }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                background: 'rgba(239,68,68,0.1)',
                                color: '#ef4444',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px',
                                border: '1px solid rgba(239,68,68,0.2)'
                            }}>
                                <User size={30} />
                            </div>
                            <p style={{ fontWeight: '700', fontSize: '16px', marginBottom: '10px' }}>
                                Utilisateur déjà en ligne
                            </p>
                            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: '1.6' }}>
                                Une autre session est active pour ce compte.
                                Voulez-vous déconnecter l'autre session pour continuer ?
                            </p>
                        </div>
                        <div className="modal-footer" style={{ borderTop: 'none', padding: '20px 30px 30px', justifyContent: 'center', gap: '15px' }}>
                            <button
                                className="btn btn-outline"
                                style={{ minWidth: '120px', color: 'white', borderColor: 'rgba(255,255,255,0.1)' }}
                                onClick={() => setShowSessionModal(false)}
                            >
                                Annuler
                            </button>
                            <button
                                className="btn btn-danger"
                                style={{ minWidth: '180px', background: '#ef4444' }}
                                onClick={() => {
                                    setShowSessionModal(false);
                                    handleSubmit(null, true);
                                }}
                            >
                                <LogIn size={16} /> Forcer la connexion
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoginPage;
